import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useSmartPolling, useWebSocketContext, type WebSocketMessage } from '@zg-rentals/web-socket-react';
import { isSatelliteWebSocketEvent, type SatelliteWebSocketEvent } from '@zg-rentals/satellite-web-socket-helpers';
import { getConversationMessagesQueryKey } from './useConversationMessagesQuery';
import { is4xxError } from '../utils/errorUtils';

const POLL_INTERVAL_MS = 60_000;

/**
 * List-level event types, separate from the `newMessage` frame the open conversation owns
 * (`useMessageListRealtimeUpdates`). List views pass this as `refetchOnEventTypes` so a received
 * message triggers a single list refetch instead of one per frame.
 */
export const LIST_MUTATING_EVENT_TYPES: ReadonlyArray<SatelliteWebSocketEvent['type']> = [
  'conversationUpdated',
  'newConversation',
];

/**
 * Wraps a list view's refetch/invalidate so realtime signals (WS events, reconnects, poll ticks)
 * don't re-run it while the list query is in a terminal 4xx — e.g. a 403 for a listing/lead the user
 * doesn't own. Refetch/invalidate re-hits the endpoint regardless of error state, so without this
 * gate one denial amplifies into a 403 storm against the backend. Pass the returned callback as
 * `useRealtimeUpdates`' `onRealtimeUpdate`.
 */
export function useGuardedRealtimeUpdate(error: unknown, onUpdate: () => void): () => void {
  return useCallback(() => {
    if (is4xxError(error)) {
      return;
    }
    onUpdate();
  }, [error, onUpdate]);
}

type Options = {
  /**
   * The conversation currently open in this view (inbox conversation id or the lead-management
   * modal's messagingConversationId), or undefined when none is open. Used only to decide which
   * conversations are "non-active" so their message caches can be invalidated — the open
   * conversation's messages are refetched by `useMessageListRealtimeUpdates` in MessageListItems.
   */
  activeConversationId: string | undefined;
  clientTimeZone: string;
  /**
   * Called on a matching satellite WebSocket event, on reconnect, and on each poll tick. Inbox
   * passes its conversation-list refetch; lead management passes its table-query invalidation.
   */
  onRealtimeUpdate: () => void;
  /**
   * Restricts which event types trigger the handler (`onRealtimeUpdate` + non-active cache
   * invalidation). Pass `LIST_MUTATING_EVENT_TYPES` so a single received message — which fans out
   * into several frames — refetches the list once, not per frame. Defaults to all types when omitted.
   */
  refetchOnEventTypes?: ReadonlyArray<SatelliteWebSocketEvent['type']>;
};

/**
 * Shared real-time update machinery for the inbox and lead-management list views, via the shared
 * satellite-websocket module. Both views subscribe to the same WebSocket and react identically:
 *   - On a matching event (see `refetchOnEventTypes`), run `onRealtimeUpdate` (refetch the
 *     conversation list / invalidate the lead table) so unread state, latest message, etc. update.
 *   - When the event targets a conversation other than the open one, invalidate that conversation's
 *     React Query messages cache so the next switch refetches fresh (the SLW global `staleTime:
 *     20_000` would otherwise serve a stale `hasUnreadMessage: false` and mark-as-read wouldn't
 *     fire on switch). See RCRM-5625.
 *
 * The open conversation's own message refetch is owned by `useMessageListRealtimeUpdates` in
 * MessageListItems, so this hook deliberately does nothing for the active conversation's messages.
 *
 * When the WebSocket isn't enabled-and-ready, falls back to polling at `POLL_INTERVAL_MS`, and
 * runs `onRealtimeUpdate` once after a transient disconnect (`errorRecoveryCount` increments) so
 * updates that arrived during the gap aren't lost.
 */
export default function useRealtimeUpdates({
  activeConversationId,
  clientTimeZone,
  onRealtimeUpdate,
  refetchOnEventTypes,
}: Options) {
  const { errorRecoveryCount, isWebSocketEnabledAndReady, subscribeToMessages } = useWebSocketContext();
  const queryClient = useQueryClient();

  // Read inputs through refs so the message handler identity stays stable when the user switches
  // conversations — otherwise every change would tear down and re-establish the WebSocket subscription.
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const clientTimeZoneRef = useRef(clientTimeZone);
  clientTimeZoneRef.current = clientTimeZone;
  const onRealtimeUpdateRef = useRef(onRealtimeUpdate);
  onRealtimeUpdateRef.current = onRealtimeUpdate;
  const refetchOnEventTypesRef = useRef(refetchOnEventTypes);
  refetchOnEventTypesRef.current = refetchOnEventTypes;

  const handleWebSocketMessage = useCallback(
    (message: WebSocketMessage) => {
      if (!isSatelliteWebSocketEvent(message.event)) return;

      // A single received message fans out into multiple frames across the subscribed topics; only
      // act on the configured types so the list view doesn't refetch once per frame.
      const eventTypes = refetchOnEventTypesRef.current;
      if (eventTypes && !eventTypes.includes(message.event.type)) return;

      onRealtimeUpdateRef.current();

      const incomingConversationId = message.event.conversationId;
      if (!incomingConversationId || incomingConversationId === activeConversationIdRef.current) return;

      // Non-active conversation received a message — invalidate its messages cache so the next time
      // the user opens it, React Query refetches fresh. See RCRM-5625.
      queryClient.invalidateQueries({
        queryKey: getConversationMessagesQueryKey({
          conversationId: incomingConversationId,
          clientTimeZone: clientTimeZoneRef.current,
        }),
      });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    return subscribeToMessages(handleWebSocketMessage);
  }, [isWebSocketEnabledAndReady, subscribeToMessages, handleWebSocketMessage]);

  // Refetch after a transient WebSocket disconnect — polling is gated on `!isWebSocketEnabledAndReady`,
  // so any updates that arrived during the gap would otherwise be lost.
  const prevErrorRecoveryCountRef = useRef(errorRecoveryCount);
  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    if (errorRecoveryCount !== prevErrorRecoveryCountRef.current) {
      prevErrorRecoveryCountRef.current = errorRecoveryCount;
      onRealtimeUpdateRef.current();
    }
  }, [isWebSocketEnabledAndReady, errorRecoveryCount]);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { updateLastPollTimestamp } = useSmartPolling({
    isEnabled: !isWebSocketEnabledAndReady,
    pollIntervalMs: POLL_INTERVAL_MS,
    refetch: () => {
      onRealtimeUpdateRef.current();
      updateLastPollTimestamp();
    },
    startPolling: (ms) => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        onRealtimeUpdateRef.current();
        updateLastPollTimestamp();
      }, ms);
    },
    stopPolling: () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    },
  });
}
